import { AfterViewInit, Component, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Patient } from '../../app/patients/patient';
import { PrintService } from '../print.service';
import { PregnancyService } from '../pregnancy.service';
import { MeasgrowthService } from '../measgrowth.service';
import { DopplerService } from '../doppler.service';
import { Anatomies } from '../anatomy/anatomy';
import { ChartDataset, ChartOptions } from 'chart.js';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NtService } from '../nt.service';
import { ExamsService } from '../exams.service';
import { FetalechoService } from '../fetalecho.service';
import { LoginService } from '../login.service';
import { UltrasoundMeasurementsService } from './../ultrasound-measurements.service';

@Component({
  selector: 'app-print',
  templateUrl: './print.component.html',
  styleUrls: ['./print.component.scss'],
  standalone: false,
})
export class PrintComponent implements OnInit, OnDestroy {
  examID: number;
  PID: number;
  now: Date;
  measures: string[];

  scores = [
    ['AFI', 'Amniotic Fluid'],
    ['Tonus', 'Tonus'],
    ['Move', 'Movements'],
    ['Breath', 'Breathing'],
  ];
  keys: string[];
  Direction = 'ltr';
  typeOfPrint = 'simple';
  chartData: ChartDataset[][][] = [];
  i: number = 0;
  cervicalMeasures;
  showCharts = false;
  // showMeas = true;
  loaded = false;
  hasImages = false;
  imPerPage = 2;

  // The summary blocks in the order they print. The last one that actually has data is rendered
  // next to the signature instead of inside the summary, so the signature can never end up alone
  // on a page - see the .signature-block group in the template.
  private readonly summaryBlockKeys = [
    'imagingQuality',
    'diagnosis',
    'followupFetalEcho',
    'diagnosisTree',
    'diagnosisGeneral',
    'recomm',
    'followupOther',
    'resultsRating',
    'terms',
  ];

  hasSummaryBlock(key: string): boolean {
    const s = this.print.summary && this.print.summary[0];
    if (!s) return false;
    const isFetalEcho = this.print.exam?.ExamType === 'Fetal Echo';
    switch (key) {
      case 'imagingQuality':
        return !!(this.print.isFetalEcho && s.ImagingQuality);
      case 'diagnosis':
        return !!s.Diagnosis;
      case 'followupFetalEcho':
        return !!(isFetalEcho && (s.Repeat1 || s.FollowUpExam));
      case 'diagnosisTree':
        return isFetalEcho
          ? !!(this.print.diagnosisList?.length > 0 || s.Anomalies)
          : !!(s.AnomaliesList?.length > 0 || s.Anomalies);
      case 'diagnosisGeneral':
        return !!(s.DiagnosisGeneral || s.DiagnosisGeneralComments);
      case 'recomm':
        return !!s.Recomm;
      case 'followupOther':
        return !!(!isFetalEcho && (s.Repeat1 || s.FollowUpExam));
      case 'resultsRating':
        return !!(!this.print.Forms && s.ResultsRating);
      case 'terms':
        return !!(!this.print.Forms && s.Terms);
      default:
        return false;
    }
  }

  private get populatedSummaryBlocks(): string[] {
    return this.summaryBlockKeys.filter((key) => this.hasSummaryBlock(key));
  }

  get lastSummaryBlock(): string | null {
    const blocks = this.populatedSummaryBlocks;
    return blocks.length ? blocks[blocks.length - 1] : null;
  }

  // True when the summary still has something to show after the last block moves to the signature.
  get summaryHasLeadingBlocks(): boolean {
    return this.populatedSummaryBlocks.length > 1;
  }

  // On the Forms path nothing moves, so every populated block stays in the summary.
  showInSummary(key: string): boolean {
    if (!this.hasSummaryBlock(key)) return false;
    return !!this.print.Forms || key !== this.lastSummaryBlock;
  }

  get imagePages(): any[][] {
    const pages: any[][] = [];
    for (let i = 0; i < this.print.images.length; i += this.imPerPage) {
      pages.push(this.print.images.slice(i, i + this.imPerPage));
    }
    return pages;
  }

  get imageCols(): number {
    return this.imPerPage <= 2 ? 1 : 2;
  }

  get imageRows(): number {
    return Math.ceil(this.imPerPage / this.imageCols);
  }
  mct = 0;
  altHeader: SafeHtml;
  showHeader = true;
  showHistory = true;
  cervicalHistory: { MeasDate: string; Cervical: number; Funneling: number | null }[] = [];
  endoHistory: { MeasDate: string; Endometrial: number; EndoStripe: number | null }[] = [];
  sub: Subscription;
  haveTitle: boolean = false;
  titleArray = [
    'Early & NT',
    '1st',
    'NT',
    '1st & NT',
    'Early',
    '2nd/3rd',
    'Weight Assessment',
    'Tech Exam',
    'Late',
    'Third Scan',
    'Specialized',
    'Brain and CNS',
    'Kidneys and Urinary Tract',
    'Polyhydramnios',
    'Skeletal',
    'Post Cesarean Placenta',
    'Gastroenteric Tract',
    'Cardiovascular',
    'Other',
    'Fetal Echo',
    'GYN',
    'Consultation Letter',
    'Amniocentesis',
    'CVS',
    'Pediatric Echo',
  ];
  console = console;

  constructor(
    private route: ActivatedRoute,
    public print: PrintService,
    public preg: PregnancyService,
    public meas: MeasgrowthService,
    public dop: DopplerService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    public nt: NtService,
    public es: ExamsService,
    public ls: LoginService,
    public us: UltrasoundMeasurementsService
  ) {
    this.now = new Date();
    this.translate.use(this.route.snapshot.params.LANG);
  }

  async ngOnInit() {
    if (this.route.snapshot.params.LANG == 'he-IL' || this.route.snapshot.params.LANG == 'ar-JO') {
      this.Direction = 'rtl';
    }
    this.typeOfPrint = this.route.snapshot.params.style;
    if (this.route.snapshot.params.charts == 'y') {
      this.print.chartsWaited.subscribe(() => {
        this.loaded = true;
      });
    } else this.loaded = true;
    let code = this.route.snapshot.params['code'];
    this.examID = this.route.snapshot.params.examid;
    this.PID = this.route.snapshot.params.PID;
    if (this.route.snapshot.params['images'] == 'y') {
      this.hasImages = true;
      this.imPerPage = Number(this.route.snapshot.params['perpage']) || 2;
    }
    if (this.route.snapshot.params['perpage'] == 'All') {
      this.hasImages = true;
      this.imPerPage = 2;
      this.print.allImages = true;
    } else this.print.allImages = false;
    if (this.route.snapshot.params.hasOwnProperty('showheader'))
      this.showHeader = this.route.snapshot.params['showheader'] == 'y';
    this.print.loadExam(this.examID, this.PID, this.hasImages, code);
    this.print.loadMsg.subscribe(() => {
      if (
        this.route.snapshot.params.charts == 'y' &&
        this.print.exam.ExamType != 'NT' &&
        this.print.exam.ExamType != '1st'
      )
        this.showCharts = true;
      if (this.print.env['htmlheader']) {
        this.altHeader = this.sanitizer.bypassSecurityTrustHtml(this.print.env['htmlheader']);
      }
      this.keys = Object.keys(this.meas.measref);
      this.keys.splice(this.keys.indexOf('EFW 2'), 1);
      if (this.print.exam?.PrintCervicalHistory) {
        this.es.getCervicalLengthHistory().subscribe({
          next: (data) => {
            this.cervicalHistory = data;
          },
        });
      }
      if (this.print.exam?.PrintEndoHistory) {
        this.es.getEndometriumHistory().subscribe({
          next: (data) => {
            this.endoHistory = data;
          },
        });
      }
    });
    let measures = this.meas.getNames();

    let sub: Subscription;

    /*if(this.meas.historyloaded && this.meas.dataloaded)
      this.getChartData();
    else{
      sub = this.meas.loaded.subscribe(()=>{
        this.getChartData();
      });
    }*/
  }

  hasScore(placenta) {
    for (let s of this.scores) {
      if (placenta['Score' + s[0]] > -1) return true;
    }
    return false;
  }

  lineChartOptions: ChartOptions = {
    responsive: false,
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        //scaleLabel: { display: true, labelString: 'weeks' }
      },
      y: {
        type: 'linear',
        //  scaleLabel: { display: true, labelString: 'mm' }
      },
    },
    aspectRatio: 1,
  };

  weightChartOptions: ChartOptions = {
    responsive: true,
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        //  scaleLabel: { display: true, labelString: 'weeks' }
      },
      y: {
        type: 'linear',
        // scaleLabel: { display: true, labelString: 'gm' }
      },
    },
    aspectRatio: 1,
  };

  getName(name: string): string {
    if (name.indexOf('EFW') > -1) {
      return 'EFW by ' + this.meas.getMeas(name).WeightType;
    }
    return name;
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  getBMI(height: number, weight: number) {
    if (!height || !weight) return '';
    return Math.round((10 * weight) / Math.pow(height / 100, 2)) / 10;
  }

  isCustomTitle(title: string): boolean {
    if (!this.es.CustomTitles || !title) {
      return false;
    }
    return this.es.CustomTitles.some((c: any) => c.Title === title);
  }
}
